// apps/web/src/lib/offline/outbox.ts — Fase 02, Plano 08 (APT-03, D-11, ADR-006).
//
// Fila local de eventos offline usando Dexie (IndexedDB) para o Coletor.
// Gerencia estados: 'pendente' | 'sincronizado' | 'conflito'.
// Não usa Dexie.Syncable (segue o protocolo próprio da Pieltec).

import Dexie, { type Table } from 'dexie';
import { apiMutate } from '../csrf-fetch';
import { classificarRespostaSync, ordenarParaEnvio, proximaSequencia } from './sync-regras';

export type OutboxStatus = 'pendente' | 'sincronizado' | 'conflito';

export interface OutboxEventRecord {
  id: string;
  idempotencyId: string;
  aggregateType: string;
  aggregateId: string;
  deviceId: string;
  deviceSequence: number;
  schemaVersion: number;
  baseVersion: number;
  payload: Record<string, unknown>;
  clientTime: string;
  status: OutboxStatus;
  conflictCode?: string;
  createdAt: number;
}

interface MetaRecord {
  chave: string;
  valor: number;
}

export class PieltecOfflineDatabase extends Dexie {
  outbox!: Table<OutboxEventRecord, string>;
  meta!: Table<MetaRecord, string>;

  constructor() {
    super('PieltecOfflineDb');
    this.version(1).stores({
      outbox: 'id, idempotencyId, aggregateId, status, deviceSequence, createdAt',
    });
    // v2 (CR-WEB-01): contador monotônico de deviceSequence por dispositivo.
    this.version(2).stores({
      outbox: 'id, idempotencyId, aggregateId, status, deviceSequence, createdAt',
      meta: 'chave',
    });
  }
}

export const offlineDb = new PieltecOfflineDatabase();

export type NovoEventoOutbox = Omit<OutboxEventRecord, 'id' | 'status' | 'createdAt' | 'deviceSequence'> & {
  /** Omitido: o outbox aloca a próxima sequência monotônica do dispositivo. */
  deviceSequence?: number;
};

/**
 * Enfileira o evento. A deviceSequence vem de um contador persistido por
 * dispositivo (alocado na mesma transação do insert), nunca do relógio de parede:
 * dois eventos no mesmo milissegundo ou um ajuste de NTP não colidem (CR-WEB-01).
 */
export async function enfileirarEvento(event: NovoEventoOutbox): Promise<OutboxEventRecord> {
  const id = crypto.randomUUID();
  return offlineDb.transaction('rw', offlineDb.meta, offlineDb.outbox, async () => {
    let deviceSequence = event.deviceSequence;
    if (deviceSequence === undefined) {
      const chave = `seq:${event.deviceId}`;
      const contador = (await offlineDb.meta.get(chave))?.valor;
      const maior = await offlineDb.outbox
        .orderBy('deviceSequence')
        .reverse()
        .filter((e) => e.deviceId === event.deviceId)
        .first();
      deviceSequence = proximaSequencia(contador, maior?.deviceSequence);
      await offlineDb.meta.put({ chave, valor: deviceSequence });
    }
    const record: OutboxEventRecord = {
      ...event,
      deviceSequence,
      id,
      status: 'pendente',
      createdAt: Date.now(),
    };
    await offlineDb.outbox.put(record);
    return record;
  });
}

export async function listarOutbox(): Promise<readonly OutboxEventRecord[]> {
  return offlineDb.outbox.orderBy('createdAt').reverse().toArray();
}

/** aggregateId (osId) -> idempotencyId dos eventos ainda pendentes de um tipo de agregado (CR-WEB-02). */
export async function listarPendentesPorAgregado(aggregateType: string): Promise<Map<string, string>> {
  const pendentes = await offlineDb.outbox.where('status').equals('pendente').toArray();
  const mapa = new Map<string, string>();
  for (const evt of ordenarParaEnvio(pendentes)) {
    if (evt.aggregateType === aggregateType && !mapa.has(evt.aggregateId)) {
      mapa.set(evt.aggregateId, evt.idempotencyId);
    }
  }
  return mapa;
}

export async function obterEstatisticasOutbox(): Promise<{
  pendentes: number;
  sincronizados: number;
  conflitos: number;
}> {
  const all = await offlineDb.outbox.toArray();
  let pendentes = 0;
  let sincronizados = 0;
  let conflitos = 0;

  for (const item of all) {
    if (item.status === 'pendente') pendentes++;
    else if (item.status === 'sincronizado') sincronizados++;
    else if (item.status === 'conflito') conflitos++;
  }

  return { pendentes, sincronizados, conflitos };
}

/** Por que a rodada de sincronização parou antes de esvaziar a fila (null = esvaziou ou nada a fazer). */
export type MotivoParadaSync = 'rede' | 'servidor' | 'autenticacao' | 'credencial' | 'autor' | null;

export interface ResultadoSincronizacao {
  processados: number;
  conflitos: number;
  /** Eventos que o servidor recusou em definitivo (4xx); ficam em 'conflito' para revisão. */
  rejeitados: number;
  /** Eventos ainda pendentes ao final da rodada. */
  restantes: number;
  /** idempotencyId dos eventos que saíram de 'pendente' nesta rodada (sincronizados ou recusados). */
  chavesFinalizadas: string[];
  parada: MotivoParadaSync;
}

interface RespostaIngestao {
  processed: number;
  conflicts: number;
  duplicates: number;
  acceptedIds: string[];
  conflictIds: string[];
  duplicateIds: string[];
}

/** Rodada em andamento + a chave (dispositivo + ator) com que foi iniciada. */
let sincronizacaoEmVoo: { chave: string; promessa: Promise<ResultadoSincronizacao> } | null = null;

/**
 * Envia os pendentes UM evento por requisição, em ordem de deviceSequence. Um evento
 * recusado (409/400/422...) vira 'conflito' e não trava os seguintes; falhas de rede,
 * 5xx ou sessão interrompem a rodada e o evento continua pendente para o próximo retry.
 * 401 de dispositivo (revoked/invalid/expired) devolve parada 'credencial': o chamador
 * deve purgar a credencial local.
 *
 * Proteção por autor (D-14): evento com `actorUserId` só é enviado quando o ator atual é
 * exatamente esse autor; ator desconhecido (sem sessão ou ainda não resolvido) também para a
 * rodada com 'autor', porque o servidor grava o apontamento em nome do usuário da sessão.
 * Chamadas concorrentes com o MESMO ator compartilham a rodada; com ator diferente, a nova
 * chamada aguarda a rodada em curso e reavalia com o ator novo (CR-02).
 */
export function sincronizarOutbox(
  authenticatedDeviceId: string,
  credential?: string,
  opcoes?: { atorAtualId?: string | null },
): Promise<ResultadoSincronizacao> {
  const chave = `${authenticatedDeviceId}|${opcoes?.atorAtualId ?? ''}`;
  if (sincronizacaoEmVoo) {
    if (sincronizacaoEmVoo.chave === chave) return sincronizacaoEmVoo.promessa;
    return sincronizacaoEmVoo.promessa
      .catch(() => undefined)
      .then(() => sincronizarOutbox(authenticatedDeviceId, credential, opcoes));
  }
  const promessa = executarSincronizacao(authenticatedDeviceId, credential, opcoes).finally(() => {
    sincronizacaoEmVoo = null;
  });
  sincronizacaoEmVoo = { chave, promessa };
  return promessa;
}

/**
 * Lista os identificadores de autor (actorUserId) com apontamentos ainda pendentes
 * na fila offline, preservando a ordem da fila e sem duplicatas.
 */
export async function autoresPendentes(deviceId?: string): Promise<string[]> {
  const pendentes = ordenarParaEnvio(await offlineDb.outbox.where('status').equals('pendente').toArray());
  const autores: string[] = [];
  const vistos = new Set<string>();
  for (const evt of pendentes) {
    if (deviceId && evt.deviceId !== deviceId) continue;
    const autor = typeof evt.payload?.actorUserId === 'string' ? evt.payload.actorUserId : null;
    if (autor && !vistos.has(autor)) {
      vistos.add(autor);
      autores.push(autor);
    }
  }
  return autores;
}

async function executarSincronizacao(
  authenticatedDeviceId: string,
  credential?: string,
  opcoes?: { atorAtualId?: string | null },
): Promise<ResultadoSincronizacao> {
  const resultado: ResultadoSincronizacao = {
    processados: 0,
    conflitos: 0,
    rejeitados: 0,
    restantes: 0,
    chavesFinalizadas: [],
    parada: null,
  };
  const pendentes = ordenarParaEnvio(await offlineDb.outbox.where('status').equals('pendente').toArray());

  for (const evt of pendentes) {
    if (evt.deviceId !== authenticatedDeviceId) {
      // Evento de outro dispositivo (credencial anterior): o servidor recusaria o envio.
      await offlineDb.outbox.update(evt.id, { status: 'conflito', conflictCode: 'DISPOSITIVO_DIVERGENTE' });
      resultado.rejeitados += 1;
      resultado.chavesFinalizadas.push(evt.idempotencyId);
      continue;
    }

    // Ator desconhecido (null/undefined) NÃO libera o envio: só eventos legados sem autor passam.
    const autorEvento = typeof evt.payload?.actorUserId === 'string' ? evt.payload.actorUserId : null;
    if (autorEvento !== null && autorEvento !== (opcoes?.atorAtualId ?? null)) {
      resultado.parada = 'autor';
      break;
    }

    let res: Response;
    try {
      res = await apiMutate('/api/v1/sync/events', {
        method: 'POST',
        headers: {
          'x-device-id': authenticatedDeviceId,
          // DeviceSyncGuard exige o par id + credencial (Plano 11-15, tablet do Kanban).
          ...(credential ? { 'x-device-credential': credential } : {}),
        },
        body: {
          events: [
            {
              id: evt.id,
              idempotencyKey: evt.idempotencyId,
              aggregateType: evt.aggregateType,
              aggregateId: evt.aggregateId,
              deviceId: evt.deviceId,
              deviceSequence: evt.deviceSequence,
              schemaVersion: evt.schemaVersion,
              baseVersion: evt.baseVersion,
              payload: evt.payload,
              clientTime: evt.clientTime,
            },
          ],
        },
      });
    } catch {
      // Offline esperado: o evento permanece pendente.
      resultado.parada = 'rede';
      break;
    }

    const corpo = (await res.json().catch(() => null)) as (Partial<RespostaIngestao> & { reason?: unknown }) | null;
    const reason = corpo && typeof corpo.reason === 'string' ? corpo.reason : null;
    const acao = classificarRespostaSync(res.status, reason);

    if (acao === 'purgar_credencial') {
      resultado.parada = 'credencial';
      break;
    }
    if (acao === 'tentar_depois_autenticacao') {
      resultado.parada = 'autenticacao';
      break;
    }
    if (acao === 'tentar_depois') {
      resultado.parada = 'servidor';
      break;
    }
    if (acao === 'rejeitar_evento') {
      await offlineDb.outbox.update(evt.id, { status: 'conflito', conflictCode: `HTTP_${res.status}` });
      resultado.rejeitados += 1;
      resultado.chavesFinalizadas.push(evt.idempotencyId);
      continue;
    }

    // Um evento por requisição: os contadores da resposta bastam (o id devolvido em
    // duplicateIds pode ser o de um registro anterior com a mesma chave de idempotência).
    if ((corpo?.conflicts ?? 0) > 0) {
      await offlineDb.outbox.update(evt.id, { status: 'conflito' });
      resultado.conflitos += 1;
      resultado.chavesFinalizadas.push(evt.idempotencyId);
    } else if ((corpo?.processed ?? 0) > 0 || (corpo?.duplicates ?? 0) > 0) {
      await offlineDb.outbox.update(evt.id, { status: 'sincronizado' });
      if ((corpo?.processed ?? 0) > 0) resultado.processados += 1;
      resultado.chavesFinalizadas.push(evt.idempotencyId);
    } else {
      // 2xx sem confirmação do evento: mantém pendente e para.
      resultado.parada = 'servidor';
      break;
    }
  }

  resultado.restantes = await offlineDb.outbox.where('status').equals('pendente').count();
  return resultado;
}
